import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@clay-run/glaze/dropdown/index';
import { Icon } from '@clay-run/glaze/icon';
import { Strong } from '@clay-run/glaze/text';
import { UNRESOLVED_PLAY_NAME } from '@shared/flow/enrollment';

import { FLOW_ICONS } from '../../icons';
import { useEnrollQueue } from '../../EnrollQueue/EnrollQueueContext';
import { formatQueueLandingBanner } from '../../EnrollQueue/queue-group-send';
import { resolveGroupName, type QueueGrouping } from '../../EnrollQueue/queue-derive';
import type { DockSendHandlers } from '../../EnrollQueue/useDockSendHandlers';
import { EmptyState } from '../ui/EmptyState';
import { ByContact } from './ByContact';
import { ByPlay } from './ByPlay';
import { matchesQueueFilter } from './leadDisplay';
import type { PlaySectionSharedProps, QueueLandingTarget } from './paneCommon';
import { FLASH_CONTACT_ATTR } from './RailRow';
import { RAIL_PANE_GAP, RailSearchRow } from './RailSearchRow';
import { UnassignedRow } from './UnassignedRow';

export interface PlaysPaneProps {
  /**
   * D39 — the dock's ONE `useDockSendHandlers()` object. This component must
   * NEVER call that hook itself: a second call stands up a second
   * `useSendGroupPipeline` over the same items, and the per-item send lock
   * (`beginSendPipeline`/`isItemBusy`) only holds while there is exactly one
   * pipeline per dock.
   */
  handlers: DockSendHandlers;
}

/**
 * The V2 rail's Plays tab (plan 2026-09-30-002, U2d) — `RailDock`'s `'queue'`
 * branch, replacing V1's `QueueBody`.
 *
 * Layout, top to bottom: a filter row (`Filter plays and contacts` +  the
 * `By Play` / `By Contact` grouping dropdown), then `Unassigned · N` when
 * there are unassigned items, then one of the two views. Everything on this
 * tab filters client-side with no network call — D13's per-tab-filter override
 * means there is no omnibox and no results surface in V2, so this box IS the
 * Plays tab's find affordance.
 *
 * Two things deliberately do NOT live here:
 *  - `useDockSendHandlers()` — passed in (D39, see `PlaysPaneProps`).
 *  - the D1 selector as a segmented control — V1's `SegmentedControl` is a
 *    two-wide strip that competes with the filter box for the rail's 320px
 *    floor; D1 picks a `Dropdown` whose trigger reads the CURRENT grouping.
 *
 * State here is all Tier-1 per the plan's Draft-State-under-SSE table: plain
 * `useState`, never seeded from `groups`, so an `artifact:updated` re-render
 * cannot move the filter, the grouping or the open row. `expandedItemId` is
 * lifted to this component precisely so it survives a grouping switch and a
 * filter change (D19, E43/E64) — a row's open/closed state is a property of
 * the REP's attention, not of whichever list is currently rendering it.
 */
export function PlaysPane({ handlers }: PlaysPaneProps) {
  const {
    groups, plays, activePlayTypes, gongFlowsSuggested,
    sendersFor, defaultSenderFor, inputOptionsFor,
    readOnly, sendReadOnly, isSending,
    assignPlay, removeItem, bulkRemove,
    unstickItem, isUnstickPending, cancelQueuedItem, isCancelPending,
    autosavePatch, flushAutosave, selectInputOption, refreshInputOptionsIfStale,
    sendSkipLabelFor, baseCopyDraftStore,
    queuePlayLanding, dismissQueuePlayLanding,
    queueFlashRequest, consumeQueueFlashRequest, requestQueueFlash,
    editStepOverride, resetStepOverrideToDefault, stepOverrideDraftFor, stepSaveErrorFor,
    subscribeStepOverrides, saveStepOverrides, clearStepOverridesForItem,
    isItemBusy, beginSendPipeline, endSendPipeline,
  } = useEnrollQueue();

  const containerRef = useRef<HTMLDivElement>(null);

  // ── D1 grouping ────────────────────────────────────────────────────────
  const [grouping, setGrouping] = useState<QueueGrouping>('by-play');
  /**
   * D4 — "has the rep chosen a grouping THIS mount?" A ref, not state:
   * flipping it must not re-render. Read (never depended on) by the landing
   * effect below so a later enroll never yanks a deliberate choice back, which
   * is `QueueBody.tsx`'s exact `groupingTouched` contract.
   */
  const groupingTouched = useRef(false);
  const chooseGrouping = (next: QueueGrouping) => {
    groupingTouched.current = true;
    setGrouping(next);
  };

  // ── D36 filter ─────────────────────────────────────────────────────────
  /**
   * `DebouncedSearchInput` is UNCONTROLLED (E67): it owns the text, this owns
   * the committed query. `ColumnsManager.tsx`'s idiom exactly. The pane
   * unmounts on a tab switch, so the filter resets with it — same lifetime
   * V1's grouping state has.
   */
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  // ── D19 lifted expansion ───────────────────────────────────────────────
  const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
  const toggleExpanded = (itemId: number) =>
    setExpandedItemId(current => (current === itemId ? null : itemId));

  const assignedGroups = useMemo(
    () => groups.filter(group => group.play_type_id !== null && group.items.length > 0),
    [groups],
  );
  const unassignedItems = useMemo(
    () => groups
      .filter(group => group.play_type_id === null)
      .flatMap(group => group.items)
      // D22 — unassigned rows respect the filter too, on identity/account/
      // email only: they have no play name to match against yet.
      .filter(item => matchesQueueFilter(item, '', q)),
    [groups, q],
  );
  const totalItems = useMemo(
    () => groups.reduce((sum, group) => sum + group.items.length, 0),
    [groups],
  );
  /** contact id → item id, for resolving the two "open this contact's editor" signals below. */
  const itemIdByContact = useMemo(() => {
    const map = new Map<string, number>();
    for (const group of groups) {
      for (const item of group.items) map.set(item.contact_id, item.id);
    }
    return map;
  }, [groups]);

  // ── D4: grouping switch on landing ─────────────────────────────────────
  const landingNonce = queuePlayLanding?.nonce ?? null;
  useEffect(() => {
    if (!queuePlayLanding) return;
    if (groupingTouched.current) return;
    // `source` is an explicit producer-set discriminator, never inferred from
    // `added === 1` — see `QueuePlayLanding.source`'s doc comment.
    setGrouping(queuePlayLanding.source === 'bulk' ? 'by-play' : 'by-contact');
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on the nonce alone (one switch per landing, never a re-run when the landing object's identity changes); `groupingTouched` is a ref read for its current value, not a reactive dependency.
  }, [landingNonce]);

  // ── D4/D12: the add-confirmation toast, once per nonce ─────────────────
  /**
   * "Which landing nonce has this instance already announced?" — a VALUE
   * marker, not lifetime state, so it is deliberately NOT reset at the top of
   * the mount effect. That is what makes StrictMode's dev double-invoke
   * (setup→cleanup→setup with no dependency change) toast exactly ONCE, while
   * a genuine remount — a new component instance with a new ref — still
   * toasts. Resetting it on mount, which is the right move for a ref carrying
   * `cancelled`/`mounted`-style lifetime state, would double-toast here.
   */
  const toastedNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!queuePlayLanding) return;
    if (toastedNonceRef.current === queuePlayLanding.nonce) return;
    toastedNonceRef.current = queuePlayLanding.nonce;
    const group = groups.find(
      candidate => candidate.play_type_id === queuePlayLanding.playTypeId
        && candidate.play_instance_ref === queuePlayLanding.playInstanceRef,
    );
    const playName = group
      ? resolveGroupName(group)
      : plays.find(
        play => play.play_type_id === queuePlayLanding.playTypeId
          && play.play_instance_ref === queuePlayLanding.playInstanceRef,
      )?.name ?? UNRESOLVED_PLAY_NAME;
    // The SAME formatter V1's in-card banner uses (D4/D30) — the add/skip
    // counts are the endpoint's real outcome tallies, never a guess.
    toast.success(formatQueueLandingBanner({
      playName,
      added: queuePlayLanding.added,
      skipped: queuePlayLanding.skipped,
    }));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on the nonce alone: `groups`/`plays` are read for the name at announce time only, and depending on them would re-run this on every queue refresh.
  }, [landingNonce]);

  const landingTarget: QueueLandingTarget | null = queuePlayLanding
    ? {
      playTypeId: queuePlayLanding.playTypeId,
      playInstanceRef: queuePlayLanding.playInstanceRef,
      nonce: queuePlayLanding.nonce,
    }
    : null;

  // ── D19: the two "open this contact's editor" signals ──────────────────
  /**
   * Durable twin of the flash signal (`AssignedRow.tsx`'s own pair of
   * effects), now resolved once here instead of per row. `handledRef` keys on
   * the SIGNAL, not on lifetime, so: one expansion per distinct target (a rep
   * who then collapses the row is not fought on the next queue refresh), and
   * a target whose item has not landed in `groups` yet is retried rather than
   * dropped.
   */
  const focusContactId = handlers.focusContactId ?? null;
  const focusHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusContactId) {
      focusHandledRef.current = null;
      return;
    }
    if (focusHandledRef.current === focusContactId) return;
    const itemId = itemIdByContact.get(focusContactId);
    if (itemId === undefined) return;
    focusHandledRef.current = focusContactId;
    setExpandedItemId(itemId);
  }, [focusContactId, itemIdByContact]);

  const openEditorNonce = queueFlashRequest?.openEditorAfterAssign ? queueFlashRequest.nonce : null;
  const openEditorContactId = queueFlashRequest?.openEditorAfterAssign ? queueFlashRequest.contactId : null;
  const openEditorHandledRef = useRef<number | null>(null);
  useEffect(() => {
    if (openEditorNonce === null || openEditorContactId === null) return;
    if (openEditorHandledRef.current === openEditorNonce) return;
    const itemId = itemIdByContact.get(openEditorContactId);
    if (itemId === undefined) return;
    openEditorHandledRef.current = openEditorNonce;
    setExpandedItemId(itemId);
  }, [openEditorNonce, openEditorContactId, itemIdByContact]);

  /**
   * The row flash, ported from `QueueBody.tsx`'s own effect (E41): scroll the
   * deep-linked contact's row into the MIDDLE of the scroller — `block:
   * 'center'`, not `'start'`, so a row never lands underneath D2's sticky
   * section header — then pulse it. Declared AFTER the expansion effects
   * above so a request carrying `openEditorAfterAssign` has already been
   * observed by the time this one consumes it.
   *
   * Keyed on the whole request object (new identity per nonce) so re-flashing
   * the same contact re-runs. Owns no lifetime ref: cleanup clears the timer
   * AND removes the class, so StrictMode's double-invoke and a genuine
   * re-flash both end with the class applied once and auto-removed. E63 — a
   * row the filter is currently hiding is the "missing row" branch: the
   * request is consumed and the filter is NOT cleared.
   */
  useEffect(() => {
    if (!queueFlashRequest) return;
    const root = containerRef.current;
    if (!root) return;
    const el = root.querySelector(`[${FLASH_CONTACT_ATTR}="${CSS.escape(queueFlashRequest.contactId)}"]`);
    if (!(el instanceof HTMLElement)) {
      consumeQueueFlashRequest(queueFlashRequest.nonce);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('animate-queue-flash');
    // Consume on COMPLETION (inside the timer), never in cleanup: StrictMode
    // runs setup→cleanup→setup before any timer fires, so a cleanup-side
    // consume would null the request and kill the second setup's flash.
    const timer = setTimeout(() => {
      el.classList.remove('animate-queue-flash');
      consumeQueueFlashRequest(queueFlashRequest.nonce);
    }, 1600);
    return () => {
      clearTimeout(timer);
      el.classList.remove('animate-queue-flash');
    };
  }, [queueFlashRequest, consumeQueueFlashRequest]);

  // E31 — a genuinely empty queue gets Terra's copy and NOTHING else: no
  // filter box and no grouping dropdown, because neither has anything to act
  // on. (A filter that matches nothing is a different state entirely — see
  // `NoQueueMatches`.)
  if (totalItems === 0) {
    return <EmptyState title="Queue is empty." description="Click Enroll on a contact to add it here." />;
  }

  const section: PlaySectionSharedProps = {
    handlers,
    readOnly,
    sendReadOnly,
    activePlayTypes,
    plays,
    baseCopyDraftStore,
    sendSkipLabelFor,
    isItemBusy,
    // The group-send SAVE phase isn't covered by `isSending` (flipped only at
    // POST time), so OR the pipeline's own pending flag in — same as V1.
    sending: isSending || handlers.groupSendPending,
    onBulkRemove: bulkRemove,
    flash: queueFlashRequest,
    focusContactId,
    expandedItemId,
    onToggleExpanded: toggleExpanded,
    inputOptionsFor,
    onSelectInputOption: selectInputOption,
    onRefreshInputOptionsIfStale: refreshInputOptionsIfStale,
    onSenderTouched: baseCopyDraftStore.markSenderTouched,
    onAutosave: autosavePatch,
    onFlushSave: flushAutosave,
    onEditStepOverride: editStepOverride,
    onResetStepOverride: resetStepOverrideToDefault,
    stepOverrideDraftFor,
    stepSaveErrorFor,
    subscribeStepOverrides,
    onSaveStepOverrides: saveStepOverrides,
    onBeginSendPipeline: beginSendPipeline,
    onEndSendPipeline: endSendPipeline,
    onUnstick: unstickItem,
    unstickPending: isUnstickPending,
    onCancel: cancelQueuedItem,
    cancelPending: isCancelPending,
    gongFlowsSuggested,
    onReassign: assignPlay,
    onClearStepOverrides: clearStepOverridesForItem,
    requestQueueFlash,
    onRemove: removeItem,
  };

  return (
    <div ref={containerRef} className={`flex flex-col ${RAIL_PANE_GAP}`}>
      {/*
        D36 — `Filter plays and contacts`, NOT `Search plays`: `PlayPicker`
        already owns that accessible name, and an unassigned row's open
        picker would make the two ambiguous in the same subtree.
        D1 — the grouping control is a Dropdown, never a segmented control.
        The trigger's accessible name is fixed (`Queue grouping`) while its
        VISIBLE text is the current grouping, so a screen reader announces
        the control and a sighted rep reads the state.
      */}
      <RailSearchRow
        aria-label="Filter plays and contacts"
        placeholder="Filter by play, campaign or contact"
        onSearch={setQuery}
        trailing={
          <Dropdown placement="bottom-end">
            <DropdownButton size="sm" variant="secondary" aria-label="Queue grouping">
              {grouping === 'by-play' ? 'By Play' : 'By Contact'}
              <Icon type={FLOW_ICONS.dropdown} size="sm" aria-hidden />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={() => chooseGrouping('by-play')}>By Play</DropdownItem>
              <DropdownItem onClick={() => chooseGrouping('by-contact')}>By Contact</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        }
      />

      {/* D22/E33 — above BOTH views, and only when there is something in it. */}
      {unassignedItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <Strong className="text-sm">{`Unassigned · ${unassignedItems.length}`}</Strong>
          {unassignedItems.map(item => (
            <UnassignedRow
              key={item.id}
              item={item}
              plays={plays}
              activePlayTypes={activePlayTypes}
              gongFlowsSuggested={gongFlowsSuggested}
              readOnly={readOnly}
              onAssign={assignPlay}
              onRemove={removeItem}
              isItemBusy={isItemBusy}
              flashRequest={queueFlashRequest}
              requestQueueFlash={requestQueueFlash}
            />
          ))}
        </div>
      )}

      {grouping === 'by-play' ? (
        <ByPlay
          groups={assignedGroups}
          q={q}
          sendersFor={sendersFor}
          defaultSenderFor={defaultSenderFor}
          section={section}
          landingTarget={landingTarget}
          onLandingScrolled={dismissQueuePlayLanding}
        />
      ) : (
        <ByContact
          groups={assignedGroups}
          q={q}
          sendersFor={sendersFor}
          defaultSenderFor={defaultSenderFor}
          section={section}
        />
      )}
    </div>
  );
}
